import { IconPlus, IconTrash } from '@developerz/ui';
import { createUniqueId, For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { OrgRole } from '../lib/org-members';
import type { RoleMapState } from '../lib/orgSsoRoleMap';
import { Field } from './Field';
import { Select } from './Select';
import { Button } from './ui/Button';

// Role-map rows on the OIDC form (#1110) — presenter only; `createRoleMapState`
// (lib/orgSsoRoleMap) owns the state and the PUT fragment. Rendered INSIDE the
// connection form, so it has no submit of its own: the form's Save persists the
// map, and "remove map" empties the rows so that save clears it server-side.

/** Roles a mapped group may grant, most privileged first (the ladder's order). */
const ROLE_OPTIONS: readonly OrgRole[] = ['owner', 'admin', 'member', 'viewer'];

export type OrgSsoRoleMapFieldsProps = {
  /** The whole controller — see `RoleMapState`. */
  readonly state: RoleMapState;
};

export function OrgSsoRoleMapFields(props: OrgSsoRoleMapFieldsProps): JSX.Element {
  const state = (): RoleMapState => props.state;
  return (
    <fieldset class="account-sso__rolemap">
      <legend class="account-sso__rolemap-title">{t('account_sso.role_map_title')}</legend>
      <p class="account-sso__rolemap-hint">{t('account_sso.role_map_hint')}</p>

      <Field label={t('account_sso.role_map_claim_label')} id="sso-role-claim">
        {(a11y) => (
          <input
            {...a11y}
            class="input"
            value={state().claim()}
            onInput={(e) => state().setClaim(e.currentTarget.value)}
            placeholder={t('account_sso.role_map_claim_placeholder')}
          />
        )}
      </Field>

      <Show
        when={state().rows().length > 0}
        fallback={<p class="list-empty">{t('account_sso.role_map_empty')}</p>}
      >
        <ul class="account-sso__rolemap-rows">
          <For each={state().rows()}>
            {(row, index) => {
              const roleId = createUniqueId();
              return (
                <li class="account-sso__rolemap-row">
                  <input
                    class="input"
                    value={row.group}
                    aria-label={t('account_sso.role_map_group_label', { n: index() + 1 })}
                    placeholder={t('account_sso.role_map_group_placeholder')}
                    onInput={(e) => state().updateRow(index(), { group: e.currentTarget.value })}
                  />
                  <span class="account-sso__rolemap-role">
                    <label id={`${roleId}-label`} for={roleId} class="sr-only">
                      {t('account_sso.role_map_role_label', { n: index() + 1 })}
                    </label>
                    <Select<OrgRole>
                      a11y={{ id: roleId }}
                      label={t('account_sso.role_map_role_label', { n: index() + 1 })}
                      block
                      value={row.role}
                      options={ROLE_OPTIONS.map((role) => ({
                        value: role,
                        label: t(`account_sso.role_${role}`),
                      }))}
                      onChange={(role) => state().updateRow(index(), { role })}
                    />
                  </span>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t('account_sso.role_map_remove_row', { n: index() + 1 })}
                    onClick={() => state().removeRow(index())}
                  >
                    <IconTrash />
                  </Button>
                </li>
              );
            }}
          </For>
        </ul>
      </Show>

      <div class="account-sso__rolemap-actions">
        <Button variant="outline" size="sm" onClick={state().addRow}>
          <IconPlus />
          {t('account_sso.role_map_add')}
        </Button>
        <Show when={state().rows().length > 0}>
          <Button variant="ghost" size="sm" onClick={state().clear}>
            {t('account_sso.role_map_clear')}
          </Button>
        </Show>
      </div>
    </fieldset>
  );
}
